import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
}

const BASE_URL = 'https://zupapost.ai';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const DEFAULT_TITLE = 'Zupa - AI-Powered Social Media Content with Psychology';
const DEFAULT_DESCRIPTION = 'Transform your content into psychologically-optimized social media posts. Zupa uses 15+ behavioral psychology principles from Cialdini & Kahneman to create engaging content for LinkedIn, Instagram, Twitter & Facebook.';

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = 'AI social media, content generator, psychology marketing, social media posts, LinkedIn content, Instagram captions',
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noIndex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | Zupa` : DEFAULT_TITLE;
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Zupa" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}

// Pre-defined SEO configurations for common pages
export const PageSEO = {
  Landing: () => (
    <SEO
      title="AI-Powered Social Media Content Generator"
      description="Transform your content into psychologically-optimized social media posts. Zupa uses 15+ behavioral psychology principles to create engaging content for LinkedIn, Instagram, Twitter & Facebook. Start your free 15-day trial today!"
      keywords="AI social media, content generator, psychology marketing, viral posts, LinkedIn content, Instagram captions, Twitter posts, Facebook marketing, B2B marketing tool"
      url="/"
    />
  ),

  Login: () => (
    <SEO
      title="Login"
      description="Log in to your Zupa account to create psychology-powered social media content that converts."
      url="/login"
      noIndex={true}
    />
  ),

  Register: () => (
    <SEO
      title="Start Your Free Trial"
      description="Create your free Zupa account and get 15 days of Pro features. No credit card required. Start creating psychology-powered content today."
      keywords="free trial, sign up, create account, AI content generator"
      url="/register"
    />
  ),

  ForgotPassword: () => (
    <SEO
      title="Reset Password"
      description="Reset your Zupa account password."
      url="/forgot-password"
      noIndex={true}
    />
  ),

  Tool: () => (
    <SEO
      title="Content Generator"
      description="Create psychology-powered social media posts for LinkedIn, Instagram, Twitter, and Facebook."
      url="/tool"
      noIndex={true}
    />
  ),

  Pricing: () => (
    <SEO
      title="Pricing Plans"
      description="Choose the perfect Zupa plan for your needs. Basic plan starts at ₹999/month. Pro plan with advanced features at ₹2,499/month. Enterprise solutions available."
      keywords="pricing, plans, subscription, AI content pricing, marketing tool cost"
      url="/#pricing"
    />
  ),

  Settings: () => (
    <SEO
      title="Settings"
      description="Manage your Zupa account settings."
      noIndex={true}
    />
  ),

  MyPosts: () => (
    <SEO
      title="My Posts"
      description="View and manage your saved social media posts."
      url="/posts"
      noIndex={true}
    />
  ),

  Collections: () => (
    <SEO
      title="Collections"
      description="Organize your posts into collections."
      url="/collections"
      noIndex={true}
    />
  ),

  Onboarding: () => (
    <SEO
      title="Setup Your Workspace"
      description="Set up your company profile and workspace to get started with Zupa."
      url="/onboarding"
      noIndex={true}
    />
  ),
};

export default SEO;
